import { Box, Button, Typography } from "@mui/material";
import { type SxProps, type Theme } from "@mui/material/styles";
import { type ReactNode } from "react";

export type QuickResponseKind = "critical" | "alert" | "cancel";

/** Solid fill for the circular icon avatar (matches tile palette; icons render white on top). */
export const avatarBg = {
  alert: "#0097a7",
  cancel: "#1976d2",
  critical: "#b71c1c",
} as const;

export const avatarCircleSx = {
  alignItems: "center",
  border: "3px solid",
  borderColor: "common.white",
  borderRadius: "50%",
  boxShadow: "0 0 0 1px rgba(0,0,0,0.08)",
  boxSizing: "border-box",
  display: "flex",
  flexShrink: 0,
  height: { md: 52, xs: 44 },
  justifyContent: "center",
  overflow: "hidden",
  width: { md: 52, xs: 44 },
} as const satisfies SxProps<Theme>;

const responseTileIconSx = {
  "& .MuiSvgIcon-root": {
    color: "common.white",
    fontSize: { md: 28, xs: 22 },
  },
  alignItems: "center",
  display: "flex",
  flexShrink: 0,
  justifyContent: "center",
} as const;

function gradientSx(kind: QuickResponseKind) {
  if (kind === "critical") {
    return {
      "&:hover": {
        backgroundColor: "transparent",
        backgroundImage: "linear-gradient(180deg, #ef5350 0%, #c62828 100%)",
      },
      backgroundColor: "transparent",
      backgroundImage: "linear-gradient(180deg, #e53935 0%, #b71c1c 100%)",
      color: "common.white",
    };
  }
  if (kind === "alert") {
    return {
      "&:hover": {
        backgroundColor: "transparent",
        backgroundImage: "linear-gradient(180deg, #4dd0e1 0%, #0097a7 100%)",
      },
      backgroundColor: "transparent",
      backgroundImage: "linear-gradient(180deg, #26c6da 0%, #00838f 100%)",
      color: "common.white",
    };
  }
  return {
    "&:hover": {
      backgroundColor: "transparent",
      backgroundImage: "linear-gradient(180deg, #fafafa 0%, #e0e0e0 100%)",
    },
    backgroundColor: "transparent",
    backgroundImage: "linear-gradient(180deg, #ffffff 0%, #eeeeee 100%)",
    borderColor: "divider",
    borderWidth: 1,
    color: "primary.main",
  };
}

export function ResponseTile({
  icon,
  imageSrc,
  imageAlt,
  label,
  kind,
  avatarBg: avatarBgProp,
  disabled,
  onPress,
  sx,
  title,
}: {
  icon?: ReactNode;
  imageSrc?: string;
  imageAlt?: string;
  label: string;
  kind: QuickResponseKind;
  /** Solid color behind the icon/GIF (defaults by kind / tile family). */
  avatarBg?: string;
  disabled?: boolean;
  onPress: () => void;
  sx?: SxProps<Theme>;
  /** Native tooltip (e.g. why a tile is disabled). */
  title?: string;
}) {
  const fill =
    avatarBgProp ?? (kind === "critical" ? avatarBg.critical : kind === "alert" ? avatarBg.alert : avatarBg.cancel);

  const visual =
    imageSrc != null && imageSrc.length > 0 ? (
      <Box
        alt={imageAlt ?? ""}
        component="img"
        decoding="async"
        loading="lazy"
        src={imageSrc}
        sx={{
          display: "block",
          height: "100%",
          maxHeight: "100%",
          maxWidth: "100%",
          objectFit: "contain",
          width: "100%",
        }}
      />
    ) : (
      <Box sx={responseTileIconSx}>{icon}</Box>
    );

  const avatar = <Box sx={[avatarCircleSx, { backgroundColor: fill }]}>{visual}</Box>;

  return (
    <Button
      disabled={disabled}
      fullWidth
      title={title}
      sx={[
        {
          alignItems: "center",
          alignSelf: "stretch",
          borderRadius: 2,
          boxShadow: 2,
          flexDirection: "row",
          fontWeight: 700,
          gap: { md: 1.25, xs: 1 },
          height: "100%",
          justifyContent: "flex-start",
          minHeight: 0,
          px: { md: 1.5, xs: 1 },
          py: { md: 1, xs: 0.75 },
          textAlign: "left",
          textTransform: "none",
          ...gradientSx(kind),
        },
        ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
      ]}
      variant={kind === "cancel" ? "outlined" : "contained"}
      onClick={onPress}
    >
      {avatar}
      <Typography
        component="span"
        sx={{
          flex: "1 1 auto",
          fontSize: { md: "1rem", xs: "0.875rem" },
          fontWeight: 700,
          lineHeight: 1.25,
          minWidth: 0,
          textAlign: "left",
        }}
      >
        {label}
      </Typography>
    </Button>
  );
}
